import { useEffect, useRef } from 'react';

import type { DecisionFormData } from './useDecisionFormData';

interface UseLabelSmartInferenceProps {
  formData: DecisionFormData;
  setFormData: (updater: (prev: DecisionFormData) => DecisionFormData) => void;
}

/**
 * Custom hook to handle smart inference for label assessments
 * - When corrected labels are provided, automatically set labelsCorrect to false
 * - When corrected labels are cleared, allow labelsCorrect to be set back to true manually
 * - Prevents inconsistent state where user provides corrections but says labels are correct
 */
export function useLabelSmartInference({ formData, setFormData }: UseLabelSmartInferenceProps) {
  // Use ref to track previous corrected labels state and prevent infinite loops
  const prevCorrectedLabelsRef = useRef<string>('');

  // Smart inference: set overall labels to false when corrected labels are provided
  useEffect(() => {
    const correctedLabelsState = formData.correctedLabels.join(',');

    // Check if state has changed to prevent infinite loops
    if (correctedLabelsState === prevCorrectedLabelsRef.current) {
      return;
    }

    // Update ref first
    prevCorrectedLabelsRef.current = correctedLabelsState;

    // If corrected labels are provided, set labelsCorrect to false
    // This indicates disagreement with original labels since corrections are being made
    const hasCorrectedLabels = formData.correctedLabels.length > 0;

    if (hasCorrectedLabels && formData.labelsCorrect !== false) {
      setFormData(prev => ({ ...prev, labelsCorrect: false }));
    }
  }, [formData.correctedLabels, formData.labelsCorrect, setFormData]);

  // Note: Only "dislike" inference is implemented (corrections → overall disagreement)
  // When corrections are cleared, labelsCorrect is NOT automatically set to true
  // Users must explicitly use the overall like/dislike buttons for positive assessments
}
